import { FC, useCallback } from 'react';
import {
  ActionButton,
  confirmationHeaders,
  DESCRIPTION_MAX_LENGTH,
  DetailLayout,
  EditLock,
  Form,
  FormAlerts,
  FormField,
  FormInput,
  GetApiErrorMessage,
  InfoIconUrl,
  NAME_MAX_LENGTH,
  ObjectHeader,
  ObjectType,
  Padding,
  Stack,
  RoleDto,
  RoleValidFormDataSchema,
  useMutationErrors,
  useAlertContext,
  useNavigationBlockers,
  validationTranslations,
  useMutateRole,
  RoleValidFormData,
  FormSegments,
  FormSegmentLabel,
  useInvalidateUserInfoQueries,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RoleForm.translations.json';
import { RoleBreadcrumbs } from './RoleBreadcrumbs';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { RoleAccessGroupFormTable } from './RoleAccessGroupFormTable';
import { RolePermissionFormTable } from './RolePermissionFormTable';

const { addTranslations, useTranslation, t } = i18nNsFactory('role-form');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type RoleFormProps =
  | {
      mode: 'create';
      id?: never;
      defaultValues: RoleDto;
      onClickCancel: () => void;
      onSuccess?: (response: RoleDto) => void;
    }
  | {
      mode: 'clone';
      id?: never;
      defaultValues: RoleDto;
      onClickCancel: () => void;
      onSuccess?: (response: RoleDto) => void;
    }
  | {
      mode: 'edit';
      id: number;
      defaultValues: RoleDto;
      onClickCancel: () => void;
      onSuccess?: (response: RoleDto) => void;
    };

export const RoleForm: FC<RoleFormProps> = (props) => {
  const { t } = useTranslation();
  const { mode, id, defaultValues, onClickCancel, onSuccess } = props;
  const { mutateAsync } = useMutateRole();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const { clear: clearBlockers } = useNavigationBlockers();
  const invalidateUserInfoQueries = useInvalidateUserInfoQueries();

  const handleFinish = useCallback(
    async (values: RoleValidFormData, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      try {
        const responseData = await mutateAsync({
          id: mode === 'edit' ? id : undefined,
          data: values,
          headers: confirmationHeaders(confirm),
        });

        if (responseData && Object.hasOwn(responseData, 'id')) {
          clearBlockers();
          setSuccess(mode === 'create' ? t('alert.success.create') : undefined);
          await invalidateUserInfoQueries();
          onSuccess?.(responseData);
        } else {
          throw new Error('Expecting "id" in response data.');
        }
      } catch (error: any) {
        setMutationErrors(error.response?.data.errors ?? []);
        throw error;
      }
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      mode,
      id,
      clearBlockers,
      setSuccess,
      t,
      invalidateUserInfoQueries,
      onSuccess,
      setMutationErrors,
    ],
  );

  return (
    <Form
      schema={RoleValidFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={mutationErrors}
      onSubmit={handleFinish}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.ROLE_DETAILS}
        breadCrumb={<RoleBreadcrumbs data={defaultValues} />}
        side={{
          header: <RoleHeader />,
          content: (
            <Padding>
              <Stack size="md">
                <FormField
                  name="name"
                  label={t('name.label')}
                  maxLength={NAME_MAX_LENGTH}
                  required
                />
                <FormField name="description" label={t('description.label')}>
                  <FormInput
                    name="description"
                    rows={2}
                    autoSize
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </FormField>
              </Stack>
            </Padding>
          ),
          footer:
            mode === 'edit' ? (
              <EditLock
                id={defaultValues.id}
                entity={defaultValues}
                objectType={ObjectType.Role}
                onTimeout={onClickCancel}
              />
            ) : undefined,
        }}
        headerConfig={{
          actions: (
            <>
              <ActionButton type="abort" onClick={onClickCancel} />
              <ActionButton type="formSubmit" />
            </>
          ),
        }}
        alert={<FormAlerts />}
      >
        <ContentWrapper>
          <FormSegments
            segments={[
              {
                value: 'permissions',
                label: (
                  <FormSegmentLabel
                    segmentValue="permissions"
                    formFieldName="permissions"
                  >
                    {t('permissions.label')}
                  </FormSegmentLabel>
                ),
                children: <RolePermissionFormTable />,
              },
              {
                value: 'accessGroups',
                label: (
                  <FormSegmentLabel
                    segmentValue="accessGroups"
                    formFieldName="accessGroups"
                  >
                    {t('accessGroups.label')}
                  </FormSegmentLabel>
                ),
                children: <RoleAccessGroupFormTable />,
              },
            ]}
          />
        </ContentWrapper>
      </DetailLayout>
    </Form>
  );
};

const RoleHeader = () => {
  const name = useWatch({ name: 'name' });

  return (
    <Padding>
      <ObjectHeader lowerText={name} upperPlaceholder="" />
    </Padding>
  );
};

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;
